import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SocialMetaTags from './SocialMetaTags';
import { getPercentageColor } from '../utils/riskUtils';
import { confChip, linkProvenance, evidenceTitle } from '../utils/walletLinkDisplay';
import { apiFetch, getApiHost } from '../utils/api';
import { EVM_CHAIN_INFO, EvmChainId, explorerUrl, networkPath, tokenPath, walletPath } from '../utils/chains';

interface NetworkInfo {
  address: string;
  name: string | null;
  exchange?: string;
  totalSize?: number;
  createdAt?: string | null;
}

interface TokenCreated {
  mint: string;
  name: string;
  symbol: string;
  uri?: string;
  uriMetadata?: any;
  creator?: string;
  creatorNetwork?: string;
  createdAt?: string;
  description?: string;
  telegram?: string;
  twitter?: string;
  website?: string;
  decimals?: number;
  networkWalletCount: number;
  networkPercentHeld: number;
  timestamp?: string;
}

interface WalletDetails {
  tags?: string[];
  walletAddress: string;
  label?: string;
  exchange?: string;
  fundingSource?: string;
  topFundingSource?: string;
  oldestTx?: string;
  oldestTxSignature?: string;
  createdAt: string;
  walletAge: string;
  topFundingNetwork?: string;
  topFundingNetworkName?: string;
  lastResearchedAt: string;
  networkInfo?: NetworkInfo;
  tokensCreatedCount: number;
  directlyFundedCount?: number;
  tokensCreated: TokenCreated[];
  snsDomains?: string[];
  // EVM wallets only
  fundingAsset?: string | null;
  fundingAmount?: number | null;
  funderIsContract?: boolean;
  topFundingPending?: boolean;
  research?: {
    figure: { figureKey: string; displayName: string; trustLevel: string | null; publicStature: string | null; publicSentiment: string | null } | null;
    figureLinks?: Array<{
      figureKey: string;
      displayName: string | null;
      trustLevel: string | null;
      confidence: 'confirmed' | 'probable' | 'claimed';
      evidence: Array<{ type: string; addedBy: string; confidence?: string; source?: string | null; basis?: string | null; vendor?: string | null; vendorValue?: string | null }>;
    }>;
    attributionSummary: string | null;
    confidence: string | null;
    identities: string[];
    linkageStrength: string | null;
    mitigation: { mitigates: boolean; basis: string | null; reviewRecommended: boolean; reason: string } | null;
    deployedCount: number | null;
    discoveredViaCount: number;
    researchedAt: string | null;
  };
}

interface ChildWallet {
  address: string;
  createdAt?: string;
  exchange?: string;
  label?: string;
  tags: string[];
  oldestTxSignature?: string;
}

interface ChildWalletsState {
  wallets: ChildWallet[];
  total: number;
  page: number;
  totalPages: number;
  loading: boolean;
}

const trustClasses = (t: string | null | undefined): string =>
  t === 'high' ? 'text-crypto-green bg-crypto-green/10' :
  t === 'medium' ? 'text-crypto-yellow bg-crypto-yellow/10' :
  t === 'low' ? 'text-crypto-red bg-crypto-red/10' :
  'text-gray-400 bg-gray-700/40';

function WalletDetailsPage({ chain }: { chain?: EvmChainId }) {
  const { walletAddress } = useParams<{ walletAddress: string }>();
  const navigate = useNavigate();
  const [walletDetails, setWalletDetails] = useState<WalletDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [children, setChildren] = useState<ChildWalletsState>({
    wallets: [], total: 0, page: 1, totalPages: 0, loading: false,
  });

  const fetchChildren = async (address: string, page: number) => {
    setChildren(prev => ({ ...prev, loading: true }));
    try {
      const res = await apiFetch(
        `${getApiHost()}/api/wallet-details/${address}/children?page=${page}&limit=20${chain ? `&chain=${chain}` : ''}`
      );
      if (res.ok) {
        const data = await res.json();
        setChildren({ wallets: data.wallets, total: data.total, page: data.page, totalPages: data.totalPages, loading: false });
      } else {
        setChildren(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setChildren(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (walletAddress) {
      fetchWalletDetails(walletAddress);
      fetchChildren(walletAddress, 1);
    }
  }, [walletAddress, chain]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWalletDetails = async (address: string) => {
    try {
      setLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/wallet-details/${address}${chain ? `?chain=${chain}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch wallet details');
      }
      const data = await response.json();
      // Bare /wallet/0x… → canonical /wallet/<chain>/0x… once the API says which chain.
      if (!chain && data.chain) { navigate(walletPath(address, data.chain), { replace: true }); return; }
      setWalletDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    let date: Date;
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      date = isoDate;
    } else {
      const timestamp = parseInt(dateString);
      date = !isNaN(timestamp) && timestamp > 0 ? new Date(timestamp) : new Date();
    }
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'now';
  };

  const formatAbsoluteDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getTokenImage = (token: TokenCreated) => {
    if (token.uriMetadata?.image) return token.uriMetadata.image;
    const symbol = (token.symbol || '?').substring(0, 4);
    const svg = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="24" fill="#374151"/><text x="24" y="28" text-anchor="middle" fill="#9CA3AF" font-size="14" font-weight="600" font-family="system-ui, -apple-system, sans-serif">${symbol}</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  const formatNumber = (num: number) => num.toLocaleString('en-US');

  if (loading) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center py-12"><div className="text-gray-400">Loading wallet details...</div></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !walletDetails) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="text-red-400 mb-4"><i className="fas fa-exclamation-triangle text-4xl"></i></div>
            <p className="text-gray-400 mb-4">Failed to load wallet details: {error}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageUrl = `${window.location.origin}${walletPath(walletAddress ?? '', chain)}`;
  const title = `Wallet ${walletAddress?.slice(0, 4)}...${walletAddress?.slice(-4)} - Wallet Analysis | Deepnets.ai`;
  const description = `View detailed information about wallet ${walletAddress?.slice(0, 4)}...${walletAddress?.slice(-4)} including funding sources and network connections.`;
  const showTopFundingNetworkLink = walletDetails.topFundingNetwork && walletDetails.topFundingNetwork !== walletAddress;
  const figure = walletDetails.research?.figure;

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <SocialMetaTags title={title} description={description} image={`${window.location.origin}/logo192.png`} url={pageUrl} type="article" />
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5 sm:space-y-6">

        {/* ─────────── 1 + 2: Identity (figure) + wallet address ─────────── */}
        <section className="bg-crypto-card rounded-xl border border-gray-700 overflow-hidden">
          {figure && (
            <div className="p-5 sm:p-6 border-b border-gray-800 bg-crypto-green/[0.03]">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-crypto-green/15 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-user-check text-crypto-green text-lg" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-2xl font-bold text-white">{figure.displayName}</span>
                      {figure.trustLevel && (
                        <span className={`text-xs font-medium capitalize px-1.5 py-0.5 rounded ${trustClasses(figure.trustLevel)}`}>{figure.trustLevel} trust</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 capitalize">
                      Known figure{figure.publicStature ? ` · ${figure.publicStature}` : ''}
                    </div>
                  </div>
                </div>
                <Link to={`/figures/${figure.figureKey}`} className="flex-shrink-0 text-sm font-medium text-crypto-accent hover:text-white bg-crypto-dark border border-gray-700 hover:border-crypto-accent rounded-lg px-3 py-1.5 transition">
                  View figure →
                </Link>
              </div>
              {walletDetails.research && (
                <>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-400">
                    {walletDetails.research.linkageStrength && <span>Linkage <span className="text-gray-200 capitalize">{walletDetails.research.linkageStrength}</span></span>}
                    {walletDetails.research.confidence && <span>Confidence <span className="text-gray-200 capitalize">{walletDetails.research.confidence}</span></span>}
                    {walletDetails.research.deployedCount != null && <span>Deployed <span className="text-gray-200">{walletDetails.research.deployedCount}</span> coins</span>}
                    {walletDetails.research.discoveredViaCount > 0 && <span>Tops <span className="text-gray-200">{walletDetails.research.discoveredViaCount}</span> tokens</span>}
                  </div>
                  {walletDetails.research.mitigation?.mitigates && (
                    <div className="mt-3 text-xs text-crypto-green bg-crypto-green/10 border border-crypto-green/20 rounded-lg px-3 py-2">
                      <i className="fas fa-shield-halved mr-1.5" />
                      Concentration mitigated — public identity is reputational collateral ({walletDetails.research.mitigation.basis}{walletDetails.research.mitigation.reviewRecommended ? ', review recommended' : ''})
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Wallet address + .sol domains */}
          <div className="p-5 sm:p-6">
            {walletDetails.label && !figure && (
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <span className="text-xl font-bold text-white">{walletDetails.label}</span>
                {walletDetails.tags?.includes('fomo_trader') && (
                  <span className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/40 rounded px-2 py-0.5 inline-flex items-center gap-1" title="Social-trading account on fomo.family; the label is its FOMO handle">
                    <i className="fas fa-fire text-[10px]"></i> FOMO trader
                  </span>
                )}
              </div>
            )}
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-2">
              {figure ? 'Wallet address' : 'Wallet'}
              {chain && <span className={`normal-case tracking-normal text-[10px] font-semibold border px-1.5 py-0.5 rounded ${EVM_CHAIN_INFO[chain].badgeClass}`}>{EVM_CHAIN_INFO[chain].short}</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a href={explorerUrl('address', walletAddress ?? '', chain)} target="_blank" rel="noopener noreferrer" className="font-mono text-sm sm:text-[15px] text-crypto-accent hover:text-crypto-accent/80 break-all">{walletAddress}</a>
              <button onClick={() => copyToClipboard(walletAddress || '')} className="text-gray-400 hover:text-white flex-shrink-0" title="Copy address"><i className="fas fa-copy" /></button>
              <a href={explorerUrl('address', walletAddress ?? '', chain)} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 flex-shrink-0" title={chain ? `View on ${EVM_CHAIN_INFO[chain].name} explorer` : "View on Solscan"}><i className="fas fa-external-link-alt text-xs" /></a>
            </div>
            {walletDetails.snsDomains && walletDetails.snsDomains.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {walletDetails.snsDomains.slice(0, 10).map(d => <span key={d} className="text-xs font-mono text-crypto-accent bg-crypto-dark border border-gray-700 rounded px-2 py-0.5">{d}</span>)}
                {walletDetails.snsDomains.length > 10 && <span className="text-xs text-gray-500 self-center">+{walletDetails.snsDomains.length - 10} more</span>}
              </div>
            )}
          </div>
        </section>

        {/* ─────────── Linked figures — every figure this wallet is tied to,
            with confidence + provenance (confirmed attribution and softer leads
            like a GMGN vendor label). ─────────── */}
        {walletDetails.research?.figureLinks && walletDetails.research.figureLinks.length > 0 && (
          <section className="bg-crypto-card rounded-xl border border-gray-700 p-5 sm:p-6">
            <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-3">Linked figures ({walletDetails.research.figureLinks.length})</h2>
            <div className="space-y-2">
              {walletDetails.research.figureLinks.map(fl => {
                const prov = linkProvenance(fl.evidence);
                return (
                  <div key={fl.figureKey} className="flex items-center gap-2 flex-wrap">
                    <Link to={`/figures/${fl.figureKey}`} className="text-sm font-semibold text-crypto-accent hover:underline">{fl.displayName || fl.figureKey}</Link>
                    <span className={`text-[10px] px-1.5 py-px rounded border ${confChip(fl.confidence)}`} title={evidenceTitle(fl.evidence)}>{fl.confidence}</span>
                    {prov && <span className="text-[10px] text-gray-500">via {prov}</span>}
                    {fl.trustLevel && <span className="text-[10px] text-gray-500 capitalize">· {fl.trustLevel} trust</span>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─────────── 3: Coins created ─────────── */}
        {walletDetails.tokensCreatedCount > 0 && (
          <section>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <i className="fas fa-coins text-crypto-accent" /> Coins Created
              <span className="text-sm font-normal text-gray-500">({formatNumber(walletDetails.tokensCreatedCount)})</span>
            </h2>
            {walletDetails.tokensCreated.length > 0 ? (
              <div className="space-y-3">
                {walletDetails.tokensCreated.map((token) => (
                  <div key={token.mint} className="bg-crypto-card rounded-xl p-4 border border-gray-700">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <img
                        src={getTokenImage(token)}
                        alt={token.name}
                        className="w-11 h-11 rounded-full border-2 border-gray-700 object-cover flex-shrink-0"
                        onError={(e) => {
                          const symbol = (token.symbol || '?').substring(0, 4);
                          const svg = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="24" fill="#374151"/><text x="24" y="28" text-anchor="middle" fill="#9CA3AF" font-size="14" font-weight="600" font-family="system-ui, -apple-system, sans-serif">${symbol}</text></svg>`;
                          (e.target as HTMLImageElement).src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
                        }}
                      />
                      <div className="flex-grow min-w-0">
                        <div className="flex items-baseline gap-2 mb-1 min-w-0">
                          <a href={tokenPath(token.mint, chain)} className="text-lg sm:text-xl font-bold text-white hover:text-crypto-accent transition-colors flex-shrink-0">{token.symbol || '???'}</a>
                          <a href={tokenPath(token.mint, chain)} className="text-sm text-gray-300 font-medium truncate hover:text-crypto-accent transition-colors">{token.name || 'Unknown Token'}</a>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-400 min-w-0">
                            <span className="whitespace-nowrap">{formatTimeAgo(token.createdAt || '')}</span>
                            <span className="text-gray-600">•</span>
                            <a href={explorerUrl('token', token.mint, chain)} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 font-mono transition-colors" title="View on explorer">{token.mint.slice(0, 4)}...{token.mint.slice(-4)}</a>
                            <button onClick={() => copyToClipboard(token.mint)} className="text-crypto-accent hover:text-crypto-accent/80 transition-colors" title="Copy mint"><i className="fas fa-copy text-xs" /></button>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-4">
                            {!chain && <div className="flex items-center gap-2">
                              <a href={`https://pump.fun/${token.mint}`} target="_blank" rel="noopener noreferrer" title="View on Pump.fun" className="opacity-60 hover:opacity-100 transition-opacity"><img src="/pumpfun.png" alt="PumpFun" className="w-4 h-4" /></a>
                              <a href={`https://gmgn.ai/sol/token/${process.env.REACT_APP_GMGN_REFERRAL_CODE}_${token.mint}`} target="_blank" rel="noopener noreferrer" title="View on GMGN" className="opacity-60 hover:opacity-100 transition-opacity"><img src="/gmgn.svg" alt="GMGN" className="w-4 h-4" /></a>
                            </div>}
                            {token.networkPercentHeld > 0 && (
                              <div className="text-right">
                                <div className={`text-base font-bold ${getPercentageColor(token.networkPercentHeld)}`}>{token.networkPercentHeld.toFixed(1)}%</div>
                                <div className="text-xs text-gray-400">{token.networkWalletCount} wallet{token.networkWalletCount !== 1 ? 's' : ''}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-crypto-card rounded-xl p-5 border border-gray-700 text-sm text-gray-400">
                This wallet created {formatNumber(walletDetails.tokensCreatedCount)} token{walletDetails.tokensCreatedCount !== 1 ? 's' : ''}, but details aren't available yet.
              </div>
            )}
          </section>
        )}

        {/* ─────────── 4: Funding (+ 4a network membership) ─────────── */}
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><i className="fas fa-hand-holding-dollar text-crypto-accent" /> Funding</h2>
          <div className="bg-crypto-card rounded-xl border border-gray-700 p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {walletDetails.fundingSource && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Funded by</div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={walletDetails.topFundingSource && walletDetails.fundingSource === walletDetails.topFundingSource ? networkPath(walletDetails.fundingSource, chain) : walletPath(walletDetails.fundingSource, chain)}
                      className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                    >
                      {walletDetails.fundingSource.slice(0, 4)}...{walletDetails.fundingSource.slice(-4)}
                    </Link>
                    <button onClick={() => copyToClipboard(walletDetails.fundingSource!)} className="text-gray-400 hover:text-white transition-colors" title="Copy funding source"><i className="fas fa-copy text-xs" /></button>
                  </div>
                </div>
              )}
              {chain && walletDetails.fundingAmount != null && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">First funding</div>
                  <div className="text-white font-semibold text-sm">
                    {walletDetails.fundingAmount.toLocaleString(undefined, { maximumFractionDigits: 6 })} {walletDetails.fundingAsset ?? ''}
                  </div>
                  {walletDetails.funderIsContract && (
                    <div className="text-xs text-gray-500 mt-0.5">From a contract (DEX, bridge or router), so the trail stops here.</div>
                  )}
                </div>
              )}
              {chain && walletDetails.topFundingPending && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Network</div>
                  <div className="text-sm text-gray-300"><i className="fas fa-circle-notch fa-spin text-xs mr-1.5" />Still tracing the funding chain to its root</div>
                </div>
              )}
              {walletDetails.label && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Label</div>
                  <div className="text-white font-semibold text-sm">{walletDetails.label}</div>
                </div>
              )}
              {walletDetails.exchange && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{walletDetails.label ? 'Funded via' : 'Exchange'}</div>
                  <div className="text-white font-semibold text-sm">{walletDetails.exchange}</div>
                </div>
              )}
              {walletDetails.oldestTxSignature && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Funding tx</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={explorerUrl('tx', walletDetails.oldestTxSignature, chain)} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors">{walletDetails.oldestTxSignature.slice(0, 4)}...{walletDetails.oldestTxSignature.slice(-4)}</a>
                    <button onClick={() => copyToClipboard(walletDetails.oldestTxSignature!)} className="text-gray-400 hover:text-white transition-colors" title="Copy tx"><i className="fas fa-copy text-xs" /></button>
                    {walletDetails.oldestTx && <span className="text-xs text-gray-500">{formatTimeAgo(walletDetails.oldestTx)}</span>}
                  </div>
                </div>
              )}
              {walletDetails.createdAt && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">First seen</div>
                  <div className="text-white text-sm font-semibold">{formatAbsoluteDate(walletDetails.createdAt)}</div>
                  <div className="text-xs text-gray-500">{formatTimeAgo(walletDetails.createdAt)}</div>
                </div>
              )}
            </div>

            {/* 4a: Funding network membership */}
            {walletDetails.networkInfo ? (
              <div className="mt-5 pt-5 border-t border-gray-800">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                  <div className="text-sm font-semibold text-white flex items-center gap-2">
                    <i className="fas fa-project-diagram text-crypto-accent" /> Funding network
                  </div>
                  <Link to={networkPath(walletDetails.networkInfo.address, chain)} className="text-sm text-crypto-accent hover:text-white transition-colors">View full network →</Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                  {walletDetails.networkInfo.name && (
                    <div className="col-span-2 sm:col-span-1">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Network</div>
                      <Link to={networkPath(walletDetails.networkInfo.address, chain)} className="text-sm font-medium text-crypto-accent hover:text-crypto-accent/80 flex items-center gap-1.5"><i className="fas fa-key text-xs" />{walletDetails.networkInfo.name}</Link>
                    </div>
                  )}
                  {walletDetails.networkInfo.totalSize && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Size</div>
                      <div className="text-white text-sm font-semibold">{formatNumber(walletDetails.networkInfo.totalSize)} wallets</div>
                    </div>
                  )}
                  {walletDetails.networkInfo.exchange && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Exchange</div>
                      <div className="text-white text-sm font-semibold">{walletDetails.networkInfo.exchange}</div>
                    </div>
                  )}
                  {walletDetails.networkInfo.createdAt && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Created</div>
                      <div className="text-white text-sm font-semibold">{new Date(walletDetails.networkInfo.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : showTopFundingNetworkLink ? (
              <div className="mt-5 pt-5 border-t border-gray-800">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Top funding network</div>
                <Link to={networkPath(walletDetails.topFundingNetwork!, chain)} className="text-sm text-crypto-accent hover:text-crypto-accent/80 flex items-center gap-1.5">
                  {walletDetails.topFundingNetworkName && <><i className="fas fa-key text-xs" />{walletDetails.topFundingNetworkName}</>}
                  <span className="font-mono">{walletDetails.topFundingNetwork!.slice(0, 4)}...{walletDetails.topFundingNetwork!.slice(-4)}</span>
                  <span>→</span>
                </Link>
              </div>
            ) : null}
          </div>
        </section>

        {/* ─────────── 5: Directly funded wallets ─────────── */}
        {children.total > 0 && (
          <section>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <i className="fas fa-sitemap text-crypto-accent" /> Directly Funded Wallets
              <span className="text-sm font-normal text-gray-500">({formatNumber(children.total)})</span>
            </h2>
            <div className="bg-crypto-card rounded-xl border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px]">
                  <thead>
                    <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-3 font-medium">Address</th>
                      <th className="px-4 py-3 font-medium">Funding Tx</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                      <th className="px-4 py-3 font-medium">Label</th>
                      <th className="px-4 py-3 font-medium">Funding Exchange</th>
                    </tr>
                  </thead>
                  <tbody>
                    {children.wallets.map((child) => (
                      <tr key={child.address} className="border-b border-gray-700/50 hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Link to={walletPath(child.address, chain)} className="font-mono text-crypto-accent hover:text-crypto-accent/80 transition-colors text-sm">{child.address.slice(0, 6)}...{child.address.slice(-6)}</Link>
                            <button onClick={() => copyToClipboard(child.address)} className="text-gray-400 hover:text-white transition-colors text-xs" title="Copy address"><i className="fas fa-copy" /></button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {child.oldestTxSignature ? (
                            <a href={explorerUrl('tx', child.oldestTxSignature, chain)} target="_blank" rel="noopener noreferrer" className="font-mono text-crypto-accent hover:text-crypto-accent/80 transition-colors">{child.oldestTxSignature.slice(0, 4)}...{child.oldestTxSignature.slice(-4)}</a>
                          ) : (<span className="text-gray-600">—</span>)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">{child.createdAt ? formatTimeAgo(child.createdAt) : '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {child.label ? (<span className="text-gray-300">{child.label}</span>) : (<span className="text-gray-600">—</span>)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {child.exchange ? (<span className="text-yellow-400">{child.exchange}</span>) : (<span className="text-gray-600">—</span>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {children.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                  <span className="text-sm text-gray-400">Page {children.page} of {children.totalPages}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => walletAddress && fetchChildren(walletAddress, children.page - 1)} disabled={children.page <= 1 || children.loading} className="px-3 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">← Prev</button>
                    <button onClick={() => walletAddress && fetchChildren(walletAddress, children.page + 1)} disabled={children.page >= children.totalPages || children.loading} className="px-3 py-1 text-sm rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Next →</button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {walletDetails.lastResearchedAt && (
          <p className="text-xs text-gray-600">Last researched {formatTimeAgo(walletDetails.lastResearchedAt)}.</p>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default WalletDetailsPage;
